import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useReportsStore } from "@/store/store";
import { pickLandingCompany, readLastCompanyId, writeLastCompanyId } from "@/lib/landing";

/**
 * The company the app is currently scoped to (AITS-540).
 *
 * Resolution order:
 *   1. the /reports/:companyId URL — authoritative while you're on it
 *   2. the Virtual Buyer store, which publishes the buyer's company on
 *      /virtual-buyer routes (those don't carry the id in the path)
 *   3. the company last opened in this workspace, remembered across reloads
 *   4. the workspace's most recently reported company
 *
 * Steps 3–4 are what keep the sidebar's company-scoped entries (Dashboard,
 * Workbench, Sources, Virtual Buyer) live on the workspace-scoped screens.
 * Before this, Settings — which has no company in its URL and unmounts the VB
 * layout on the way in — left them greyed out and unclickable, so entering
 * Settings was a dead end you could only leave via Home.
 *
 * Whatever (1) or (2) resolves is written back, so the selection survives a
 * reload and a trip through Settings.
 */
export const useActiveCompany = (): number | null => {
  const location = useLocation();
  const activeVbCompanyId = useReportsStore((s) => s.activeVbCompanyId);
  const { activeWorkspace } = useWorkspace();

  const workspaceId = activeWorkspace?.id ?? null;
  const companies = activeWorkspace?.companies;

  // The company the current route is explicitly about, if any.
  const routeCompanyId = useMemo(() => {
    const match = location.pathname.match(/^\/reports\/(\d+)/);
    if (match?.[1]) return Number(match[1]);
    return activeVbCompanyId;
  }, [location.pathname, activeVbCompanyId]);

  // The membership check gates only what we REMEMBER, not what we return.
  //
  // Persisting matters because the stored id outlives the page: switching
  // workspace while a /reports/:id URL is still on screen would file that
  // company under the workspace it isn't in, and every later session would
  // build sidebar links from it.
  //
  // The returned value deliberately trusts the URL instead. If you are on a
  // company's report, that IS the company you're working in — and the backend,
  // not this hook, decides whether you may open its pages. Gating the return
  // too would blank the whole nav whenever `companies` is momentarily empty
  // (a failed hydration), which is the exact dead-end this hook exists to
  // prevent.
  //
  // Derived as a boolean so the effect depends on a primitive: `companies` is a
  // fresh array on every `refreshWorkspaces()` and would otherwise re-fire the
  // write each time.
  const routeCompanyIsInWorkspace =
    routeCompanyId != null && (companies?.some((c) => c.company_id === routeCompanyId) ?? false);

  useEffect(() => {
    if (!routeCompanyIsInWorkspace || workspaceId == null) return;
    writeLastCompanyId(workspaceId, routeCompanyId);
  }, [routeCompanyIsInWorkspace, routeCompanyId, workspaceId]);

  return useMemo(() => {
    if (routeCompanyId != null) return routeCompanyId;
    const company = pickLandingCompany(activeWorkspace, readLastCompanyId(workspaceId));
    return company?.company_id ?? null;
  }, [routeCompanyId, activeWorkspace, workspaceId]);
};
